import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import config from "../../config";
import noimage from '../../assets/images/Desktop placeholder.svg';
import ServiceForm from "./ServiceForm";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import styles from './CateringServiceforuser.module.css';
import {
  createusepackage,
  updateusepackage,
  deleteuserpackage,
  getuserpackage,
  getCateringProviderServices,
  getCateringProviderCategories,
  getCateringItemMasterList,
  getCateringCategoryList
} from "../../Apiservices/Auth/servers/fetchProviderData";

function CateringServiceforuser({ onNext, onClose, quantityformparent, isResuming = false, selectedCuisineType, selectedMeal }) {
  // console.log('🔄 CateringServiceforuser rendering', { onNext, onClose, isResuming });

  const [includedItems, setIncludedItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allMasterItems, setAllMasterItems] = useState([]);
  const [userPackages, setUserPackages] = useState([]);
  const [editServices, setEditServices] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedcategory, setselectedcategory] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [mealTypes, setMealTypes] = useState([]);
  const [quantity, setQuantity] = useState(quantityformparent ?? 1);

  const fetchUserPackagesRef = useRef(null);

  const filteredServices = useMemo(() => {
    if (!selectedcategory) return [];

    const filtered = userPackages.filter((service) => {
      // Must match selected meal
      if (service.meal?.meal_id !== selectedcategory) return false;

      // If a cuisine type is selected, also filter by it
      if (selectedCuisineType && Number(selectedCuisineType) > 0) {
        const cuisineId = Number(selectedCuisineType);
        const packageCuisineId =
          service.catering_package_cuisine_master_id ??
          service.cuisineMasterId ??
          service.cuisine_master_id ??
          service.catering_package_cuisine_master?.id ??
          null;
        // If the package has no cuisine set, include it (generic package)
        // If it has a cuisine set, only show if it matches
        if (packageCuisineId != null && Number(packageCuisineId) !== cuisineId) {
          return false;
        }
      }

      return true;
    });

    // Deduplicate by package_id to prevent duplicate cards
    const seen = new Set();
    const unique = [];
    for (const service of filtered) {
      if (service.package_id && !seen.has(service.package_id)) {
        seen.add(service.package_id);
        unique.push(service);
      }
    }

    return unique;
  }, [selectedcategory, selectedCuisineType, userPackages]);

  // Handle package selection
  const handlePackageSelect = useCallback((packageData) => {
    console.log("📦 Package selected for booking:", packageData, quantity);

    const finalQuantity = parseInt(quantity, 10) || 1;
    if (onNext) {
      onNext(packageData, finalQuantity);
    }
  }, [onNext, quantity]);

  // Handle searching providers without a user-defined package (show vendor packages directly)
  const handleSearchWithoutPackage = useCallback(() => {
    const finalQuantity = parseInt(quantity, 10) || 1;
    if (onNext) {
      // Send a minimal package object with just meal/cuisine so backend can match vendor packages
      const minimalPackage = {
        meal: { meal_id: selectedcategory },
        included_items: [],
        custom_combo_name: '',
      };
      onNext(minimalPackage, finalQuantity);
    }
  }, [onNext, quantity, selectedcategory]);

  // Handle back/cancel
  const handleBack = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Handle create new package
  const handleCreateNew = useCallback(() => {
    setShowCreateForm(true);
    window.scrollTo({
      top: 450,
      behavior: 'smooth'
    });
  }, []);

  // Handle form cancel
  const handleFormCancel = useCallback(() => {
    setShowCreateForm(false);
  }, []);

  // Handle edit package
  const handleEdit = useCallback((packageId) => {
    setEditServices(prev => ({
      ...prev,
      [packageId]: true
    }));
    setTimeout(() => {
      window.scrollTo({
        top: 450,
        behavior: 'smooth'
      });
    }, 100);
  }, []);

  // Handle cancel edit
  const handleCancel = useCallback((packageId) => {
    setEditServices(prev => {
      const newState = { ...prev };
      delete newState[packageId];
      return newState;
    });
  }, []);

  // Handle delete package
  const handleDelete = useCallback(async (packageId) => {
    setLoading(true);
    console.log("checking delete click")
    try {
      await deleteuserpackage(packageId);
      if (fetchUserPackagesRef.current) {
        await fetchUserPackagesRef.current();
      }
    } catch (error) {
      console.error("Failed to delete user package:", error);
      alert('Failed to delete package. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreateService = useCallback(async (formData) => {
    try {
      console.log("📦 Creating user package with data:", formData);

      // Prepare data for the user packages API
      const userPackageData = {
        item_name: formData.custom_combo_name,
        custom_combo_name: formData.custom_combo_name,
        meal_id: Number(selectedcategory),
        included_items: formData.included_items.map(item => ({
          itemName: item.itemName,
          qty: parseInt(item.qty) || 1,
          itemId: item.itemId
        })),
        included_side_items: []
      };

      // Persist cuisine style association so the package is retrievable by cuisine filter
      const cuisineId = Number(selectedCuisineType);
      if (Number.isFinite(cuisineId) && cuisineId > 0) {
        userPackageData.catering_package_cuisine_master_id = cuisineId;
        userPackageData.cuisine_master_id = cuisineId;
      }

      // Persist meal association from selectedMeal prop if available
      const mealId = Number(selectedMeal);
      if (Number.isFinite(mealId) && mealId > 0 && !userPackageData.meal_id) {
        userPackageData.meal_id = mealId;
      }

      console.log("🚀 Sending to createusepackage:", userPackageData);
      const response = await createusepackage(userPackageData);

      console.log("✅ Package created successfully:", response);

      if (fetchUserPackagesRef.current) {
        await fetchUserPackagesRef.current();
      }

      setShowCreateForm(false);

    } catch (error) {
      console.error('❌ Error creating user package:', error);
      alert('Failed to create package. Please try again.');
    }
  }, [selectedcategory, selectedCuisineType, selectedMeal]);

  // Handle edit service
  const handleEditService = useCallback(async (formData) => {
    try {
      console.log("formData", formData)
      const updateData = {
        package_id: formData.package_id,
        custom_combo_name: formData.custom_combo_name,
        meal_id: selectedcategory,
        item_name: formData.custom_combo_name,

        included_items: formData.included_items
          .filter(item => item.itemName && item.qty)
          .map(item => ({
            itemName: item.itemName,
            qty: parseInt(item.qty) || 1,
            itemId: item.itemId || item.catering_item_id || null,
            user_package_id: item.user_package_id || null,
          })),
        included_side_items:[]
      };

      console.log("🔄 Updating package with data:", updateData);
      const response = await updateusepackage(updateData);

      if (fetchUserPackagesRef.current) {
        await fetchUserPackagesRef.current();
      }

      setEditServices(prev => {
        const newState = { ...prev };
        delete newState[formData.package_id];
        return newState;
      });

    } catch (error) {
      console.error('❌ Error updating user package:', error);
      alert('Failed to update package. Please try again.');
    }
  }, [selectedcategory]);

  // Handle form submit
  const handleFormSubmit = useCallback((formData, isEditMode = false) => {
    if (isEditMode) {
      handleEditService(formData);
    } else {
      handleCreateService(formData);
    }
  }, [handleEditService, handleCreateService]);

  // Check if service is being edited
  const isServiceEditing = useCallback((packageId) => {
    return !!editServices[packageId];
  }, [editServices]);

  const isAnyServiceEditing = useMemo(() => {
    return Object.values(editServices).some(editing => editing);
  }, [editServices]);

  const isAnyServiceCreating = useMemo(() => {
    return showCreateForm;
  }, [showCreateForm]);

  // Data fetching functions
  const fetchMealTypes = useCallback(async () => {
    const mealTypes = await getCateringProviderCategories();

    if (mealTypes) {
      setMealTypes(mealTypes);
      fetchCategories(mealTypes[0]?.meal_id)
    }
  }, []);

  const fetchCategories = useCallback(async (selected_meal_type_id) => {
    const categories = await getCateringCategoryList(selected_meal_type_id);

    if (categories) {
      setCategories(categories);
    }
  }, []);

  // Fetch user packages
  const fetchUserPackages = useCallback(async () => {
    setLoading(true);
    try {
      const userPackagesData = await getuserpackage();
      console.log("userPackages", userPackagesData);

      if (userPackagesData && userPackagesData.data) {
        setUserPackages(userPackagesData.data);
      }

      // Set initial category if available
      const includedItemsMap = includedItems;
      if (includedItemsMap.length > 0 && !selectedcategory) {
        setselectedcategory(includedItemsMap[0].meal_id);
        fetchCategories(includedItemsMap[0].meal_id);
      }

    } catch (error) {
      console.error("Failed to fetch user packages:", error);
    } finally {
      setLoading(false);
    }
  }, [includedItems, selectedcategory]);

  useEffect(() => {
    fetchUserPackagesRef.current = fetchUserPackages;
  }, [fetchUserPackages]);

  const resultedServicesFunc = useCallback(async () => {
    setLoading(true);
    try {
      const cateringServices = await getCateringProviderServices();

      const includedItemsMap = cateringServices?.map((meal) => {
        return {
          meal_id: meal.meal_id,
          meal_name: meal.meal_name,
          defaults: meal.defaults,
          imageid: meal.image?.id || null,
        };
      }) || [];

      console.log("cateringServices includedItemsMap", includedItemsMap);

      if (includedItemsMap.length > 0) {
        setselectedcategory(includedItemsMap[0].meal_id);
        fetchCategories(includedItemsMap[0].meal_id);
      }

      setIncludedItems(includedItemsMap);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Use getCateringItemMasterList to get proper catering_item_id
        const [masterItems] = await Promise.all([
          getCateringItemMasterList({
            typeId: selectedCuisineType,
            mealId: selectedMeal
          })
        ]);

        // Process main items (non-side items) - similar to Flutter implementation
        const processedMainItems = (masterItems || [])
          .filter(categoryGroup =>
            categoryGroup?.category?.id !== null &&
            categoryGroup?.category?.name !== 'Uncategorized'
          )
          .map(categoryGroup => {
            const categoryInfo = categoryGroup.category;
            const categoryId = categoryInfo?.id;
            const categoryName = categoryInfo?.name || 'Uncategorized';

            const items = (categoryGroup.items || []).map(item => ({
              id: item.id,
              itemId: item.id,
              itemName: item.itemName || "",
              name: item.itemName || "",
              qty: 1,
              categoryId: categoryId,
              categoryName: categoryName,
              description: item.description || "",
              isActive: item.isActive,
              isSideItem: item.isSideItem || false,
              dietCategoryName: item.cuisineCategory?.name || null,
              itemGroupId: item.itemGroupId || 0,
              itemGroupName: item.itemGroupName || "",
              itemSubGroupId: item.itemSubGroupId || 0,
              itemSubGroupName: item.itemSubGroupName || "",
              itemGroup: item.itemGroup || null,
              isCounter: item.isCounter || false,
              price: item.price || null
            }));

            return {
              category: {
                id: categoryId,
                name: categoryName
              },
              items: items
            };
          });

        setAllMasterItems(processedMainItems);

      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchDropdownData();
    fetchMealTypes();
  }, []);

  useEffect(() => {
    setEditServices({});
    setShowCreateForm(false);
  }, [selectedcategory]);

  useEffect(() => {
    if (fetchUserPackagesRef.current) {
      fetchUserPackagesRef.current();
    }
    resultedServicesFunc();
  }, [resultedServicesFunc]);

  // ServiceForm props
  const getServiceFormProps = useCallback((service, isEditing) => {
    const baseProps = {
      isPackageMode: false,
      service: service,
      categories: includedItems,
      includedItemsList: userPackages,
      itemsCategories: categories,
      allMasterItems: allMasterItems,
      sidessallMasterItems: [],
      selectedcategory: selectedcategory,
      types: mealTypes,
    };

    const formMode = isEditing ? "edit" : "view";

    return {
      ...baseProps,
      onSubmit: handleFormSubmit,
      onCancel: () => handleCancel(service.package_id),
      isEditing: isEditing,
      onToggleEdit: () => handleEdit(service.package_id),
      mode: formMode,
      onPackageSelect: onNext ? () => handlePackageSelect(service) : undefined,
      deleteButton: !isEditing ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("🗑️ Direct delete for packageId:", service.package_id);
            handleDelete(service.package_id);
          }}
          className={styles.deletebtn}
          title="Delete this package"
        >
          <DeleteTwoToneIcon fontSize="small" /> Delete
        </button>
      ) : null
    };
  }, [
    includedItems,
    userPackages,
    allMasterItems,
    [],
    selectedcategory,
    mealTypes,
    handlePackageSelect,
    handleFormSubmit,
    handleCancel,
    handleEdit,
    handleDelete,
    onNext
  ]);

  // Render create form
  const renderCreateForm = () => {
    if (showCreateForm) {
      return (
        <ServiceForm
          isPackageMode={false}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          categories = {includedItems}
          includedItemsList={userPackages}
          itemsCategories={categories}
          allMasterItems={allMasterItems}
          sidessallMasterItems={[]}
          selectedcategory={selectedcategory}
          types={mealTypes}
          mode="create"
        />
      );
    }
    return null;
  };

  // Render service cards
  const renderServiceCards = () => {
    if (!showCreateForm && filteredServices.length > 0) {
      return filteredServices.map((service) => {
        const isEditing = isServiceEditing(service.package_id);

        // If any service is being edited or created, only show that one
        if ((isAnyServiceEditing || isAnyServiceCreating) && !isEditing) {
          return null;
        }

        const serviceFormProps = getServiceFormProps(service, isEditing);

        return (
          <div
            key={service.package_id}
            className={isEditing ? styles.editServiceCard : styles.serviceCard}
          >
            <ServiceForm {...serviceFormProps} />
          </div>
        );
      });
    }

    // No packages message

    return null;
  };

  return (
    <div className={styles.cateringContainer}>
      {/* Header */}
      <div className={styles.cateringHeader}>
        <button
          type="button"
          onClick={handleBack}
          className={styles.backButton}
          title="Go back"
        >
          <i className="pi pi-arrow-left"></i> Back
        </button>

        <h3 className={styles.cateringTitle}>
          {onNext ? "Select Catering Package" : "Your Catering Packages"}
        </h3>

        <i></i>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <h3 style={{ textAlign: "center" }}>
            <i
              className="pi pi-spin pi-spinner-dotted"
              style={{ fontSize: "2rem", color: "#0cc0df" }}
            ></i>
          </h3>
          <p>Loading catering packages...</p>
        </div>
      ) : (
        <>
          <div className={styles.leftContainer}>
            {includedItems.length > 0 ? (
              includedItems.map((item) => (
                <div
                  key={item.meal_id}
                  className={
                    selectedcategory === item.meal_id
                      ? `${styles.categoryItem} ${styles.active}`
                      : styles.categoryItem
                  }
                  onClick={() => {
                    setselectedcategory(item.meal_id);
                    fetchCategories(item.meal_id);
                  }}
                >
                  <img
                    src={item.imageid ? `${config.API_BASE_URL}/gallery/download/${item.imageid}` : noimage}
                    alt={item.meal_name}
                    className={styles.categoryImage}
                    onError={(e) => {
                      e.target.src = noimage;
                    }}
                  />
                  <div className={styles.itemLabel}>{item.meal_name}</div>
                </div>
              ))
            ) : (
              <p>No meal categories available</p>
            )}
          </div>

          <div className={styles.rightContainer}>
            <div className={styles.rightContainerWrapper}>
              {selectedcategory ? (
                <>
                  {/* Add New Package Button - Only when not in edit/create state and not in selection mode */}
                  {!onNext && !showCreateForm && !isAnyServiceEditing && (
                    <div className={styles.addNewContainer}>
                      <button
                        onClick={handleCreateNew}
                        className={styles.addNewBtn}
                      >
                        + Add New Package
                      </button>
                    </div>
                  )}

                  {/* Package Info - Only when in selection mode */}
                  {onNext && !showCreateForm && !isAnyServiceEditing && (
                    <div className={styles.packageInfo}>
                      <p>Select a package from the available options below after choosing no of people</p>
                    </div>
                  )}
                  {onNext && !showCreateForm && !isAnyServiceEditing && (
                    <div className={styles.input_dialog}>
                      <label>Number of People:</label>
                      <input
                        className={styles.quantity_input}
                        value={quantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "") {
                            setQuantity("");
                          } else if (/^\d+$/.test(val)) {
                            setQuantity(parseInt(val, 10));
                          }
                        }}
                        placeholder="please enter quantity"
                        min="1"
                      />
                    </div>
                  )}
                  {filteredServices.length >= 0 &&
                    !showCreateForm && !isAnyServiceEditing && (
                    <button onClick={handleCreateNew} className={styles.createFirstBtn}>
                      + Create Your Package
                    </button>
                  )}

                  {/* Skip to provider search when in booking mode */}
                  {onNext && !showCreateForm && !isAnyServiceEditing && selectedcategory && (
                    <div className={styles.orDivider}>
                      <span>or</span>
                    </div>
                  )}
                  {onNext && !showCreateForm && !isAnyServiceEditing && selectedcategory && (
                    <button
                      onClick={handleSearchWithoutPackage}
                      className={styles.findCaterersBtn}
                    >
                      <i className="pi pi-search" style={{ marginRight: 6 }}></i>
                      Find Available Caterers
                    </button>
                  )}
                  {/* Create Form */}
                  {renderCreateForm()}


                  {/* Service Cards */}
                  <div className={
                    isAnyServiceEditing || isAnyServiceCreating ?
                      styles.editServiceCards :
                      styles.serviceCards
                  }>
                    {renderServiceCards()}
                  </div>

                  {/* No packages and not creating */}
                  {filteredServices.length === 0 && !showCreateForm && !onNext && (
                    <div className={styles.noPackages}>
                      <p>No packages created yet for this category.</p>
                      <button onClick={handleCreateNew} className={styles.createFirstBtn}>
                        Create Your First Package
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.selectCategory}>
                  <p>Please select a meal category to {onNext ? "view packages" : "manage packages"}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default React.memo(CateringServiceforuser);